import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth-context";
import { api, fmtCurrency } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, ArrowUp, ArrowDown, Wallet as WalletIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";

export default function ConsumerWallet() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [wallet, setWallet] = useState(null);
    const [txns, setTxns] = useState([]);
    const [open, setOpen] = useState(null); // 'topup' | 'send'

    const refresh = async () => {
        const w = await api.get("/wallet");
        setWallet(w.data);
        const t = await api.get("/wallet/transactions");
        setTxns(t.data);
    };
    useEffect(() => { refresh(); }, []);

    // Auto-open dialog based on URL
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    useEffect(() => {
        if (path.endsWith("/add")) setOpen("topup");
        else if (path.endsWith("/send")) setOpen("send");
    }, [path]);

    return (
        <div data-testid="consumer-wallet-page">
            <MobileHeader title={t("home.onepass_wallet")} subtitle={`${t("common.currency")} · ${user?.currency || "NGN"}`} backTo="/consumer">
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4">
                    <div className="text-xs uppercase tracking-wider text-white/70">{t("wallet.balance")}</div>
                    <div className="font-display text-3xl font-bold mt-1" data-testid="wallet-balance-big">
                        {wallet ? fmtCurrency(wallet.balance, wallet.currency, { minDecimals: 2, maxDecimals: 2 }) : "—"}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                        <Action icon={Plus} label={t("home.add_money")} onClick={() => setOpen("topup")} testid="topup-btn" />
                        <Action icon={ArrowUp} label={t("home.send")} onClick={() => setOpen("send")} testid="send-btn" />
                    </div>
                </div>
            </MobileHeader>

            <div className="px-5 mt-4">
                <h2 className="font-display font-semibold text-[#0A1930] mb-3">{t("wallet.recent_txns")}</h2>
                <div className="space-y-2">
                    {txns.length === 0 && <div className="text-center text-sm text-slate-500 py-8">{t("wallet.no_txns")}</div>}
                    {txns.map((tx) => (
                        <div key={tx.id} data-testid={`txn-${tx.category}`} className="bg-white rounded-2xl p-4 border border-slate-200/60 flex items-center gap-3">
                            <div className={`size-10 rounded-xl flex items-center justify-center ${tx.type === "credit" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                {tx.type === "credit" ? <ArrowDown size={18} /> : <ArrowUp size={18} />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{tx.description}</div>
                                <div className="text-xs text-slate-500 capitalize">{tx.category}</div>
                            </div>
                            <div className={`font-display font-bold text-sm ${tx.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                                {tx.type === "credit" ? "+" : "−"} {fmtCurrency(tx.amount, wallet?.currency || "NGN")}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <ActionDialog
                kind={open}
                onClose={() => setOpen(null)}
                onDone={async () => { setOpen(null); await refresh(); }}
                currency={wallet?.currency || "NGN"}
                t={t}
            />
        </div>
    );
}

function Action({ icon: Icon, label, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className="bg-white/15 backdrop-blur rounded-xl p-3 text-center text-white tap-feedback">
            <Icon size={20} className="mx-auto" />
            <div className="text-[11px] font-semibold mt-1">{label}</div>
        </button>
    );
}

function ActionDialog({ kind, onClose, onDone, currency, t }) {
    const [amount, setAmount] = useState("");
    const [counterparty, setCounterparty] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const a = parseFloat(amount);
        if (!a || a <= 0) return toast.error(t("wallet.invalid_amount"));
        setBusy(true);
        try {
            if (kind === "topup") {
                const r = await api.post("/wallet/topup", { amount: a, method: "mobile_money", description: "Wallet top-up via Mobile Money" });
                toast.success(t("wallet.added", { amount: fmtCurrency(a, currency) }));
                // Auto-route notifications
                if (Array.isArray(r.data?.auto_routed)) {
                    r.data.auto_routed.forEach((ar) => {
                        toast.success(
                            `${ar.emoji || "🎯"} ${ar.percent}% auto-routed to ${ar.goal_name}${ar.completed ? " — goal reached!" : ""}`,
                            { duration: 5000 }
                        );
                    });
                }
            } else {
                if (!counterparty) {
                    setBusy(false);
                    return toast.error(t("wallet.recipient"));
                }
                await api.post("/wallet/send", { amount: a, counterparty, description: `Transfer to ${counterparty}` });
                toast.success(t("wallet.sent_to", { amount: fmtCurrency(a, currency), to: counterparty }));
            }
            setAmount("");
            setCounterparty("");
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!kind} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">{kind === "topup" ? t("wallet.add_money_t") : t("wallet.send_money_t")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    {kind === "send" && (
                        <div>
                            <label className="text-sm font-medium">{t("wallet.recipient")}</label>
                            <Input
                                value={counterparty}
                                onChange={(e) => setCounterparty(e.target.value)}
                                placeholder={t("wallet.recipient_placeholder")}
                                className="mt-1.5 h-11 rounded-xl"
                                data-testid="action-counterparty"
                            />
                        </div>
                    )}
                    <div>
                        <label className="text-sm font-medium">{t("wallet.amount")} ({currency})</label>
                        <Input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                            className="mt-1.5 h-11 rounded-xl"
                            data-testid="action-amount"
                        />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#0A1930] hover:bg-[#0d1f3d]" data-testid="action-submit">
                        {busy ? t("common.processing") : kind === "topup" ? t("home.add_money") : t("home.send")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
